import { NumberLimitsSettings } from './WorkflowBuilder.types';

export const DAILY_CALL_CAP_MIN = 1;
export const DAILY_CALL_CAP_MAX = 1000;

/** Backend cap for a fully active number when the workflow stores no dailyCallCap. */
export const DAILY_CALL_CAP_DEFAULT = 100;

export interface NumberLimitsFormValues {
    /** null = never stored, so it is not sent unless the user touches it. */
    numberLimitsSkipWarmup: boolean | null;
    /** '' = never stored. */
    numberLimitsDailyCallCap: string;
}

export const parseNumberLimitsFormValues = (nl?: NumberLimitsSettings | null): NumberLimitsFormValues => ({
    numberLimitsSkipWarmup: typeof nl?.skipWarmup === 'boolean' ? nl.skipWarmup : null,
    numberLimitsDailyCallCap: typeof nl?.dailyCallCap === 'number' ? String(nl.dailyCallCap) : '',
});

/** Blank is unset and always allowed; anything else must be a whole number in range. */
export const validateDailyCallCap = (value: string): string | true => {
    if (value === undefined || value === null || String(value).trim() === '') return true;
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < DAILY_CALL_CAP_MIN || parsed > DAILY_CALL_CAP_MAX) {
        return `Enter a whole number between ${DAILY_CALL_CAP_MIN} and ${DAILY_CALL_CAP_MAX}`;
    }
    return true;
};

const isDefault = (values: NumberLimitsFormValues): boolean =>
    !values.numberLimitsSkipWarmup && values.numberLimitsDailyCallCap.trim() === '';

const hasStored = (values: NumberLimitsFormValues): boolean =>
    values.numberLimitsSkipWarmup !== null || values.numberLimitsDailyCallCap.trim() !== '';

/**
 * What to send for numberLimits: undefined omits the key, null clears to defaults,
 * an object replaces the stored one and so carries every field set in the form.
 */
export const buildNumberLimitsUpdate = (
    current: NumberLimitsFormValues,
    loaded: NumberLimitsFormValues
): NumberLimitsSettings | null | undefined => {
    const currentCap = current.numberLimitsDailyCallCap.trim();
    if (
        current.numberLimitsSkipWarmup === loaded.numberLimitsSkipWarmup &&
        currentCap === loaded.numberLimitsDailyCallCap.trim()
    ) {
        return undefined;
    }
    if (isDefault(current)) return hasStored(loaded) ? null : undefined;

    const update: NumberLimitsSettings = {};
    if (current.numberLimitsSkipWarmup !== null) update.skipWarmup = current.numberLimitsSkipWarmup;
    if (currentCap !== '') {
        const parsed = Number(currentCap);
        if (!Number.isInteger(parsed)) return undefined;
        update.dailyCallCap = parsed;
    }
    return update;
};
